import { useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Users, Check, Plus, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useNetwork } from '@/contexts/NetworkContext'
import { useNetworkProfile } from '@/contexts/NetworkProfileContext'
import { useCurrentUser } from '@/contexts/UserContext'
import { isSuperAdmin } from '@/data/superAdmins'
import { parseAdnDefaults } from '@/services/networks/networkFieldSettings'
import { AuthService } from '@/services/core/auth'
import { SiteGroupsApi } from '@/services/sites/siteGroupsApi'
import { SitesApi } from '@/services/sites/sitesApi'
import { hiddenTargetingTypes, targetingForSave } from '@/services/targeting/targeting'
import {
  draftToTeamFees,
  FEE_TEAM_TYPES,
  parseFeeTemplates,
  teamFeesToDraft,
} from '@/services/users/teamFees'
import { TeamGroupsApi } from '@/services/users/teamGroupsApi'
import { availableTeamTypes, TeamsApi, TeamInput, Team } from '@/services/users/teamsApi'
import { ApiError, apiErrorDescription } from '@/services/core/apiError'
import { COUNTRIES } from '../../data/countries.tsx'
import { InlineEditTitle } from '../../components/InlineEditTitle'
import { AppShell } from '../../components/AppShell'
import { FormSkeleton } from '../../components/FormSkeleton'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import { NetworkObjectLookup } from '../../components/NetworkObjectLookup'
import { StatsBar, StatsBarId, StatsBarActivity } from '../../components/StatsBar'
import { Card, CardHeader, CardIcon, CardTitle, CardContent } from '@/components/ui/card'
import { ObjectTypeLabel } from '../../components/ObjectTypeLabel'
import { formatDate } from '../../utils/format'
import {
  ObjectStateToggle,
  useSaveObjectState,
  type ObjectState,
} from '../../components/ObjectStateToggle'
import { VersionHistoryButton } from '@/components/VersionHistoryButton'
import { ValidationWarnings } from '@/components/ValidationWarnings'
import { CurrencySelect } from '@/components/CurrencySelect'
import { FieldLabel } from '@/components/FieldLabel'
import { LabelAutocomplete } from '@/components/LabelAutocomplete'
import { TargetingCard } from '@/components/targeting/TargetingCard'
import { TeamFeesCard } from '@/components/TeamFeesCard'
import { TeamMembersCard } from '@/components/TeamMembersCard'
import { TeamSitesCard, type SiteRef } from '@/components/TeamSitesCard'

/** Team types whose users can be given sites to deliver on. */
const SITE_MEMBER_TYPES = ['STANDARD', 'AX_ADVERTISER']

/** What the team is linked to elsewhere: its group, its sites, its site groups. */
interface TeamLinks {
  teamGroupId: string
  teamGroups: { id: string; name: string }[]
  memberSites: SiteRef[]
  /** Sites the team is in that it owns or that no team owns yet. */
  ownableSites: SiteRef[]
  ownedSiteIds: string[]
  ownedSiteGroups: SiteRef[]
}

const NO_LINKS: TeamLinks = {
  teamGroupId: '',
  teamGroups: [],
  memberSites: [],
  ownableSites: [],
  ownedSiteIds: [],
  ownedSiteGroups: [],
}

async function fetchTeamLinks(team: Team): Promise<TeamLinks> {
  const type = team.type || 'STANDARD'
  const [teamGroups, sites, siteGroups] = await Promise.all([
    TeamGroupsApi.getAllPages(undefined, 'ACTIVE'),
    // Only these types show the team's sites, and a team can be on thousands of them.
    SITE_MEMBER_TYPES.includes(type) || type === 'AX_PUBLISHER'
      ? SitesApi.getTeamSites(team.id)
      : [],
    SiteGroupsApi.getAllPages(undefined, 'all'),
  ])
  const sameType = teamGroups.filter((group) => group.type === type)
  return {
    teamGroupId: sameType.find((group) => group.teams.some((t) => t.id === team.id))?.id ?? '',
    teamGroups: sameType.map(({ id, name }) => ({ id, name })),
    memberSites: sites.map(({ id, name }) => ({ id, name })),
    ownableSites: sites
      .filter((site) => !site.ownerTeamId || site.ownerTeamId === team.id)
      .map(({ id, name }) => ({ id, name })),
    ownedSiteIds: sites.filter((site) => site.ownerTeamId === team.id).map((site) => site.id),
    ownedSiteGroups: siteGroups
      .filter((group) => group.ownerTeamId === team.id)
      .map(({ id, name }) => ({ id, name })),
  }
}

const diff = (before: string[], after: string[]) => ({
  added: after.filter((id) => !before.includes(id)),
  removed: before.filter((id) => !after.includes(id)),
})

// ── Inner form component ────────────────────────────────────────────────────

interface TeamEditFormProps {
  team: Team | null
  links: TeamLinks
  id: string
  isNew: boolean
}

function TeamEditForm({ team, links, id, isNew }: TeamEditFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentNetwork } = useNetwork()

  const teamTypes = useMemo(() => {
    const types = availableTeamTypes(currentNetwork)
    // Keep a saved team's type even if the network no longer offers it.
    return team?.type && !types.includes(team.type) ? [...types, team.type] : types
  }, [currentNetwork, team?.type])

  // A fixed fee is quoted in every network currency, the default first.
  const currencies = useMemo(() => {
    const available = currentNetwork?.availableCurrencies ?? []
    const preferred = currentNetwork?.defaultCurrency
    return preferred && available.includes(preferred)
      ? [preferred, ...available.filter((c) => c !== preferred)]
      : available
  }, [currentNetwork])

  // Super admins can start a team's fees from a network fee template.
  const superAdmin = isSuperAdmin(useCurrentUser()?.username)
  const { networkProfile } = useNetworkProfile()
  const feeTemplates = useMemo(
    () => (superAdmin ? parseFeeTemplates(parseAdnDefaults(networkProfile?.profile)) : []),
    [superAdmin, networkProfile]
  )

  const form = useForm({
    defaultValues: {
      name: team?.name ?? '',
      description: team?.description ?? '',
      externalReference: team?.externalReference ?? '',
      type: team?.type ?? (teamTypes.includes('STANDARD') ? 'STANDARD' : (teamTypes[0] ?? '')),
      country: team?.country ?? '',
      defaultCurrency: team?.defaultCurrency ?? '',
      labels: team?.labels ?? [],
      teamGroupId: links.teamGroupId,
      fees: teamFeesToDraft(team),
      defaultTargeting: team?.defaultTargeting ?? {},
      memberSites: links.memberSites,
      ownedSiteIds: links.ownedSiteIds,
    },
    onSubmit: ({ value }) => mutation.mutate(value),
  })

  const saveObjectState = useSaveObjectState(
    (objectState) => TeamsApi.update(id, { name: team?.name ?? '', objectState }),
    ['team', id],
    ['teams-all']
  )
  const { isDirty, markClean, blocker } = useUnsavedChanges(form)

  const mutation = useMutation({
    mutationFn: async (value: typeof form.state.values) => {
      const input: TeamInput = {
        name: value.name,
        description: value.description,
        externalReference: value.externalReference,
        type: value.type || undefined,
        defaultCurrency: value.defaultCurrency || undefined,
        labels: value.labels,
        objectState: (team?.objectState ?? 'ACTIVE') as ObjectState,
        ...(value.type === 'AX_ADVERTISER'
          ? {
              country: value.country || null,
              defaultTargeting: targetingForSave(value.defaultTargeting),
            }
          : {}),
        ...draftToTeamFees(value.fees, value.type),
      }
      const saved = isNew ? await TeamsApi.create(id, input) : await TeamsApi.update(id, input)
      if (isNew) return saved

      // The team's links live on the other objects, so each is saved there.
      if (value.teamGroupId !== links.teamGroupId) {
        await (value.teamGroupId
          ? TeamGroupsApi.setTeam(value.teamGroupId, id)
          : TeamGroupsApi.setTeam(links.teamGroupId, id, true))
      }
      const members = diff(
        links.memberSites.map((s) => s.id),
        value.memberSites.map((s) => s.id)
      )
      await SitesApi.setTeamMembership(id, members.added, members.removed)
      const owned = diff(links.ownedSiteIds, value.ownedSiteIds)
      await SitesApi.setOwnerTeam(owned.added, id)
      await SitesApi.setOwnerTeam(owned.removed, null)
      return saved
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['team', id], data)
      queryClient.invalidateQueries({ queryKey: ['teams-all'] })
      queryClient.invalidateQueries({ queryKey: ['team-links', id] })
      queryClient.invalidateQueries({ queryKey: ['team-groups'] })
      markClean()
      if (isNew) navigate(`/admin/teams/${id}`, { replace: true })
      toast.success(t('common.saved'), {
        description: isNew ? t('teams.createdSuccess') : t('teams.savedSuccess'),
        duration: 3000,
      })
    },
    onError: (error) => {
      const summary =
        error instanceof ApiError
          ? error.message
          : isNew
            ? t('teams.createFailed')
            : t('teams.saveFailed')
      const detail = apiErrorDescription(error)
      toast.error(summary, { description: detail, duration: 5000 })
    },
  })

  const crumbs = [
    { label: t('teams.title'), href: '/admin/teams' },
    { label: isNew ? t('teams.newTitle') : (team?.name ?? t('teams.editTitle')) },
  ]

  return (
    <AppShell crumbs={crumbs} showBack>
      <form
        className="ad-page"
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
      >
        <ValidationWarnings warnings={team?.validationWarnings} />
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <ObjectTypeLabel type={t('objectTypes.team')} />
            <h1>
              <form.Field
                name="name"
                validators={{
                  onChange: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                  onSubmit: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                }}
              >
                {(field) => (
                  <InlineEditTitle
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    placeholder={t('teams.namePlaceholder')}
                    required
                    error={field.state.meta.errors[0] as string | undefined}
                  />
                )}
              </form.Field>
            </h1>
          </div>
          {team && !isNew && (
            <div className="ad-page__actions">
              <VersionHistoryButton />
              <ObjectStateToggle value={team!.objectState ?? 'ACTIVE'} onChange={saveObjectState} />
            </div>
          )}
        </div>

        {team && !isNew && (
          <StatsBar
            items={[
              {
                label: t('common.id'),
                content: <StatsBarId value={team.id} />,
              },
              ...(team.createTime && team.updateTime
                ? [
                    {
                      label: t('stats.activity'),
                      content: (
                        <StatsBarActivity
                          created={formatDate(team.createTime)}
                          updated={formatDate(team.updateTime)}
                        />
                      ),
                    },
                  ]
                : []),
            ]}
          />
        )}

        <form.Subscribe selector={(s) => s.values.type}>
          {(type) => (
            <div className="ad-edit-columns">
              <div className="ad-edit-columns__info">
                <Card>
                  <CardHeader>
                    <CardTitle>
                      <CardIcon icon={Users} />
                      {t('teams.details')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="field-grid">
                      <div className="p-field full">
                        <FieldLabel>{t('common.description')}</FieldLabel>
                        <form.Field name="description">
                          {(field) => (
                            <Textarea
                              rows={2}
                              value={field.state.value}
                              onChange={(e) => field.handleChange(e.target.value)}
                              placeholder={t('teams.descriptionPlaceholder')}
                            />
                          )}
                        </form.Field>
                      </div>
                      <div className="p-field full">
                        <FieldLabel>{t('teams.externalReference')}</FieldLabel>
                        <form.Field name="externalReference">
                          {(field) => (
                            <Input
                              value={field.state.value}
                              onChange={(e) => field.handleChange(e.target.value)}
                            />
                          )}
                        </form.Field>
                      </div>
                      <div className="p-field full">
                        <FieldLabel
                          tooltip={type ? t(`teams.typeHelp.${type}`, '') || undefined : undefined}
                          hint={isNew ? undefined : t('teams.typeLocked')}
                        >
                          {t('common.type')}
                        </FieldLabel>
                        <form.Field name="type">
                          {(field) => (
                            <Select
                              label={t('common.type')}
                              value={field.state.value}
                              disabled={!isNew}
                              onValueChange={(v) => field.handleChange(v ?? '')}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={t('teams.selectType')}>
                                  {field.state.value
                                    ? t(`teams.types.${field.state.value}`, field.state.value)
                                    : null}
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                {teamTypes.map((teamType) => (
                                  <SelectItem key={teamType} value={teamType}>
                                    {t(`teams.types.${teamType}`, teamType)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </form.Field>
                      </div>
                      {type === 'AX_ADVERTISER' && (
                        <div className="p-field full">
                          <FieldLabel>{t('common.country')}</FieldLabel>
                          <form.Field name="country">
                            {(field) => (
                              <Select
                                label={t('common.country')}
                                value={field.state.value}
                                onValueChange={(v) =>
                                  field.handleChange(v === '__clear__' ? '' : (v ?? ''))
                                }
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder={t('teams.selectCountry')}>
                                    {COUNTRIES.find((o) => o.value === field.state.value)?.label ??
                                      null}
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__clear__">&mdash;</SelectItem>
                                  {COUNTRIES.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>
                                      {o.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </form.Field>
                        </div>
                      )}
                      {!isNew && (
                        <div className="p-field full">
                          <FieldLabel tooltip={t('teams.teamGroupHelp')}>
                            {t('teams.teamGroup')}
                          </FieldLabel>
                          {links.teamGroups.length ? (
                            <form.Field name="teamGroupId">
                              {(field) => (
                                <Select
                                  label={t('teams.teamGroup')}
                                  value={field.state.value}
                                  onValueChange={(v) =>
                                    field.handleChange(v === '__clear__' ? '' : (v ?? ''))
                                  }
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder={t('teams.selectTeamGroup')}>
                                      {links.teamGroups.find((g) => g.id === field.state.value)
                                        ?.name ?? null}
                                    </SelectValue>
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__clear__">&mdash;</SelectItem>
                                    {links.teamGroups.map((g) => (
                                      <SelectItem key={g.id} value={g.id}>
                                        {g.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            </form.Field>
                          ) : (
                            <p className="text-xs text-muted-foreground">
                              {t('teams.noTeamGroups')}
                            </p>
                          )}
                        </div>
                      )}
                      <div className="p-field full">
                        <FieldLabel>{t('teams.currency')}</FieldLabel>
                        <form.Field name="defaultCurrency">
                          {(field) => (
                            <CurrencySelect
                              value={field.state.value}
                              onValueChange={(v) => field.handleChange(v)}
                              label={t('teams.currency')}
                            />
                          )}
                        </form.Field>
                      </div>
                      <div className="p-field full">
                        <FieldLabel>{t('common.labels')}</FieldLabel>
                        <form.Field name="labels">
                          {(field) => (
                            <LabelAutocomplete
                              value={field.state.value}
                              onChange={(v) => field.handleChange(v)}
                            />
                          )}
                        </form.Field>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {FEE_TEAM_TYPES.includes(type) && (
                  <form.Field name="fees">
                    {(field) => (
                      <TeamFeesCard
                        value={field.state.value}
                        onChange={(v) => field.handleChange(v)}
                        teamType={type}
                        currencies={currencies}
                        templates={feeTemplates}
                      />
                    )}
                  </form.Field>
                )}

                {type === 'AX_ADVERTISER' && (
                  <form.Field name="defaultTargeting">
                    {(field) => (
                      <TargetingCard
                        targeting={field.state.value}
                        onChange={(targeting) => field.handleChange(targeting)}
                        hiddenTypes={hiddenTargetingTypes(currentNetwork)}
                      />
                    )}
                  </form.Field>
                )}
              </div>

              {!isNew && (
                <div className="flex min-w-0 flex-1 flex-col gap-3.5">
                  <TeamMembersCard kind="team" id={id} />
                  {(SITE_MEMBER_TYPES.includes(type) ||
                    type === 'AX_PUBLISHER' ||
                    links.ownedSiteGroups.length > 0) && (
                    <form.Subscribe
                      selector={(s) => [s.values.memberSites, s.values.ownedSiteIds] as const}
                    >
                      {([memberSites, ownedSiteIds]) => (
                        <TeamSitesCard
                          members={
                            SITE_MEMBER_TYPES.includes(type)
                              ? {
                                  value: memberSites,
                                  onChange: (sites) => form.setFieldValue('memberSites', sites),
                                }
                              : undefined
                          }
                          ownership={
                            type === 'AX_PUBLISHER'
                              ? {
                                  value: ownedSiteIds,
                                  onChange: (ids) => form.setFieldValue('ownedSiteIds', ids),
                                  candidates: links.ownableSites,
                                }
                              : undefined
                          }
                          siteGroups={links.ownedSiteGroups}
                        />
                      )}
                    </form.Subscribe>
                  )}
                </div>
              )}
            </div>
          )}
        </form.Subscribe>

        <div
          className={`detail-savebar${isDirty || isNew || mutation.isPending ? ' is-visible' : ''}`}
        >
          <div className="detail-savebar__left">
            {mutation.isPending && (
              <>
                <Loader2 className="size-4 animate-spin" />
                {isNew ? t('common.creating') : t('common.saving')}
              </>
            )}
          </div>
          <div className="detail-savebar__actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/admin/teams')}
              disabled={mutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {isNew ? <Plus className="size-4" /> : <Check className="size-4" />}
              {isNew ? t('teams.createTeam') : t('common.save')}
            </Button>
          </div>
        </div>
      </form>
      <UnsavedChangesDialog blocker={blocker} />
    </AppShell>
  )
}

// ── Outer page ──────────────────────────────────────────────────────────────

export function TeamEditPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const isNew = searchParams.get('new') === 'true'
  const hasToken = AuthService.isAuthenticated()

  const {
    data: team,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['team', id],
    queryFn: () => TeamsApi.getById(id!),
    enabled: !!id && !isNew && hasToken,
    retry: false,
  })

  // The form starts from these, so it waits for them.
  // On failure the form falls back to no links, which it then leaves untouched.
  const {
    data: links,
    isPending: linksPending,
    isError: linksError,
  } = useQuery({
    queryKey: ['team-links', id],
    queryFn: () => fetchTeamLinks(team!),
    enabled: !!team && !isNew,
  })

  const crumbsLoading = [
    { label: t('teams.title'), href: '/admin/teams' },
    { label: isNew ? t('teams.newTitle') : t('teams.editTitle') },
  ]

  if (isError && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <NetworkObjectLookup objectId={id!} />
      </AppShell>
    )
  }

  if ((isLoading || (!!team && linksPending && !linksError)) && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <FormSkeleton rows={2} columns={2} />
      </AppShell>
    )
  }

  return (
    <TeamEditForm key={id} team={team ?? null} links={links ?? NO_LINKS} id={id!} isNew={isNew} />
  )
}
